import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { SegmentedControl, type SegmentedOption } from '@/components/ui/SegmentedControl'
import type { YearFilter } from '@/types/domain'

/* THE STATEMENT YEAR — a second date vocabulary, on purpose.
 *
 * DateRangeControl's 1M/3M/1Y/all is a window measured back from today, and
 * that file forbids a fifth value in ITS list for a good reason. A client
 * statement is not a window: it is the book for a calendar year, the thing a
 * client is sent to reconcile against, and "the last twelve months" is a
 * period no accountant has ever asked for. So the year lives here, declared
 * once, and nowhere else.
 *
 * It lives in the URL rather than in useViewState: a statement for 2025 is a
 * link somebody pastes into a chat, and a stored preference is the opposite of
 * that. */

const ALL = 'all'

/** Reads `?year=`. Absent or unreadable is the current year — the book that
 *  is open — never "all", which would make a stale bookmark load every case
 *  the company ever had. */
/* eslint-disable-next-line react-refresh/only-export-components -- the parser
   lives with the control it parses for. L2 puts the statement year in one
   module; splitting it so hot reload can keep this file's component state is
   how a second year vocabulary gets started. */
export function parseYearParam(raw: string | null, current: number): YearFilter {
  if (raw === ALL) return ALL
  const n = Number(raw)
  if (raw !== null && Number.isInteger(n) && n >= 2000 && n <= current + 1) return n
  return current
}

/* eslint-disable-next-line react-refresh/only-export-components -- same file,
   same reason: the hook is the URL half of this control and has no other home. */
export function useYearParam(): [YearFilter, (next: YearFilter) => void] {
  const [searchParams, setSearchParams] = useSearchParams()
  const year = parseYearParam(searchParams.get('year'), new Date().getUTCFullYear())
  const setYear = useCallback(
    (next: YearFilter) => {
      const params = new URLSearchParams(searchParams)
      params.set('year', String(next))
      // Replace: paging through years is one visit, not five back-button stops.
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams],
  )
  return [year, setYear]
}

export function YearControl({
  years,
  value,
  onChange,
  label,
}: {
  /** Newest first, from the server. The selected year is always offered, so
   *  a bookmark to a year with no cases still shows what it asked for. */
  years: number[]
  value: YearFilter
  onChange: (next: YearFilter) => void
  label: string
}) {
  const { t } = useTranslation()
  const options = useMemo<readonly SegmentedOption<string>[]>(() => {
    const listed = new Set(years)
    if (value !== ALL) listed.add(value)
    const sorted = [...listed].sort((a, b) => b - a)
    return [
      // Years are notation, so a literal label rather than a catalogue key.
      ...sorted.map((y) => ({ value: String(y), label: String(y) })),
      { value: ALL, label: t('clients.year.all') },
    ]
  }, [years, value, t])

  return (
    <SegmentedControl
      options={options}
      value={String(value)}
      onChange={(next) => onChange(next === ALL ? ALL : Number(next))}
      label={label}
    />
  )
}
